import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { InputDetails } from '@app/inputs/inputs-api.model';
import { isOutcomeTile } from '@app/profile/components/profile-collection/profile-collection-reactive-store/utils';
import { FeedbackService } from '@app/feedback/services/feedback.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TranslateService } from '@ngx-translate/core';
import { InputsService } from '@app/inputs/services/inputs.service';
import { AuthService } from '@app/shared/services/auth.service';
import { Profile } from '@app/profile/profile.model';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { FocusStackService } from '@app/shared/services/focus-stack.service';
import { A11yService, ClipboardService } from '@dg/shared-services';
import { WindowToken } from '@app/shared/window.token';
import { UserOutcomeActionsService } from '@app/profile/components/user-outcome-tile-v2/user-outcome-actions/user-outcome-actions.service';

export interface DeleteModalPayload {
  contentCategory: string;
  contentType: string;
  itemId: number;
  userItemId: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileOverviewActionsService {
  public i18n = this.translate.instant([
    'Core_GeneralErrorMessage',
    'Core_Edit',
    'Core_YesSure',
    'Core_Delete',
    'Core_ReportAProblem',
    'Core_Select',
    'Pathways_PathwayAccessError',
    'UserProfileSvc_ProblemDeletingCollectionItem',
    'UserProfileSvc_ProblemUpdatingCollectionItem',
    'Core_Settings',
    'ProfileCtrl_ShareProfileSuccess',
    'ProfileCtrl_ShareProfile',
  ]);

  public inputSelectedSubject$ = new Subject();
  public outcomeSelectedSubject$ = new Subject();

  constructor(
    private translate: TranslateService,
    private notifierService: NotifierService,
    private feedbackService: FeedbackService,
    private inputsService: InputsService,
    private authService: AuthService,
    private genericModalService: InputModalDispatcherService,
    private focusStackService: FocusStackService,
    private clipboardService: ClipboardService,
    private a11yService: A11yService,
    private userOutcomeActionsService: UserOutcomeActionsService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  public copyProfileUrl(owner: Profile, showSuccessNotification = false): void {
    const ownerUrl = owner.vanityUrl;
    let profileUrl = this.windowRef.location.origin;
    profileUrl += `/profile/${ownerUrl}`;
    this.clipboardService.copyToClipboard(profileUrl);
    this.a11yService.announcePolite(this.i18n.ProfileCtrl_ShareProfile);
    if (showSuccessNotification) {
      this.notifierService.showSuccess(
        this.i18n.ProfileCtrl_ShareProfileSuccess
      );
    }
  }

  /**
   * Show the report a problem modal for an item
   * @param event - The click event from the menu
   * @param itemId - The id of the item to report
   * @param itemType - The type of item being reported
   * @param refocusElement
   */
  // used by UserExperienceActions, SkillSignalMoreButton, UserOutcomeActions
  public showReportModal(
    event: Event,
    itemId: number,
    itemType: string,
    refocusElement: HTMLElement
  ): Observable<any> {
    return this.feedbackService.showReportProblemModal(
      {
        itemType:
          this.feedbackService.getFeedbackItemTypeFromResource(itemType),
        itemId: itemId,
      },
      refocusElement
    );
  }

  public selectOutcome(item: any) {
    this.outcomeSelectedSubject$.next(item);
  }

  public selectInput(item: any) {
    this.inputSelectedSubject$.next(item);
  }

  // Used by ProfileLearning, ProfileCollection
  public modifyInputMenuOptions = (
    isOwner: boolean,
    showSelect: boolean,
    content,
    defaultConfig
  ) => {
    const authUser = this.authService.authUser;
    const shareOption = defaultConfig.find((c) => c.title === 'Share');

    if (shareOption) {
      const hideShareDefault = shareOption.isHidden();
      shareOption.isHidden = () =>
        hideShareDefault || content.model.inputType === 'Position';
    }

    return [
      {
        title: this.i18n.Core_Select,
        isHidden: () => !authUser || !isOwner || !showSelect,
        defaultAction: (_, popoverTrigger) => {
          if (isOutcomeTile(content.contentType)) {
            this.selectOutcome(content);
          } else {
            this.selectInput(content);
          }
        },
      },
      {
        title: this.i18n.Core_Edit,
        isHidden: () =>
          !authUser ||
          !isOwner ||
          content.resourceType === 'Task' ||
          content.resourceType === 'Article' ||
          content.resourceType === 'Event' ||
          content.resourceType === 'Book',
        defaultAction: (_, popoverTrigger) => {
          this.editLearningContentGenericModal(content);
        },
      },
      {
        title: this.i18n.Core_Delete,
        isHidden: () => !authUser || !isOwner,
        defaultAction: (event, popoverTrigger) => {
          this.deleteLearningContent(content, popoverTrigger);
        },
        preventRefocus: true,
      },
      ...defaultConfig,
    ];
  };

  private editLearningContentGenericModal(content): void {
    this.genericModalService
      .edit({
        inputId: content.model.inputId,
        userInputId: content.model.userInputId,
        inputType: content.model.inputType,
        initialModel: {
          inputId: content.model.inputId,
          userInputId: content.model.userInputId,
          inputType: content.model.inputType,
          externalCompletionOnly: content.model.externalCompletionOnly,
        },
      })
      .subscribe({
        next: (item: any) => {
          // dismissed
          if (!item) {
            return;
          }
          this.inputsService.notifyInputModified(content.model.inputType);
          // No need for notifierService notification here. The new generic Input modal system handles that internally.
        },
        error: (e) => {
          if (e) {
            console.error(e);
          }
        },
      });
  }

  private deleteLearningContent(content, popoverTrigger): void {
    this.focusStackService.push(popoverTrigger?.nativeElement);
    const inputDetails = content.model as InputDetails;
    const headerText = this.translate.instant(
      'confirmModal_Delete' + inputDetails.inputType + 'Title'
    );
    const bodyText = this.translate.instant(
      'confirmModal_Delete' + inputDetails.inputType + 'Body'
    );

    const payload: DeleteModalPayload = {
      contentCategory: 'Input',
      contentType: inputDetails.resourceType,
      itemId: inputDetails.inputId,
      userItemId: inputDetails.userInputId,
    };
    this.userOutcomeActionsService.showDeleteModal(
      headerText,
      bodyText,
      payload,
      content.model.title
    );
  }
}
